<template>
    <div class="flex flex-wrap justify-start">
        <portal to="page-header">{{ $t('Dashboard') }}</portal>

        <div class="sm:px-1 md:px-3 lg:px-3 xl:px-3 mb-1 sm:mb-1 md:mb-3 lg:mb-3 xl:mb-3 w-full sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/2">
            <card class="flex items-center">
                <div class="p-3 mr-4 text-orange-500 bg-orange-100 rounded-full dark:text-orange-100 dark:bg-orange-500">
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"></path>
                    </svg>
                </div>
                <div>
                    <p class="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                        {{ $t('Total active players count') }}
                    </p>
                    <p class="text-lg font-semibold text-gray-700 dark:text-gray-200">
                        {{ playerCount }}
                    </p>
                </div>
            </card>
        </div>
        <div class="sm:px-1 md:px-3 lg:px-3 xl:px-3 mb-1 sm:mb-1 md:mb-3 lg:mb-3 xl:mb-3 w-full sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/2">
            <card class="flex items-center">
                <div class="p-3 mr-4 text-green-500 bg-green-100 dark:text-green-100 dark:bg-green-500">
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"></path>
                    </svg>
                </div>
                <div>
                    <p class="mb-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                        {{ $t('Current players total balance') }}
                    </p>
                    <p v-for="(balance, currency) in balances" class="text-lg font-semibold text-gray-700 dark:text-gray-200">
                        {{  showFormattedAmount(balance, currency) }}
                    </p>
                </div>
            </card>
        </div>
        <div class="sm:px-1 md:px-3 lg:px-3 xl:px-3 mb-1 sm:mb-1 md:mb-3 lg:mb-3 xl:mb-3 w-full sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/2">
            <card class="flex-1">
                <div class="mb-4">
                    <h2 class="font-bold">{{ $t('Summary per location') }}</h2>
                </div>
                <div class="mb-4">
                    <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {{ $t('Deposits total sum') }}
                    </p>
                    <p v-for="(deposit, currency) in operatorStats.sumOfDeposits" class="text-lg font-semibold text-gray-700 dark:text-gray-200">
                        {{  showFormattedAmount(deposit, currency) }}
                    </p>
                </div>
                <div class="mb-4">
                    <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {{ $t('Withdrawals total sum') }}
                    </p>
                    <p v-for="(withdrawal, currency) in operatorStats.sumOfWithdrawals" class="text-lg font-semibold text-gray-700 dark:text-gray-200">
                        {{  showFormattedAmount(withdrawal, currency) }}
                    </p>
                </div>
                <div>
                    <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {{ $t('Withdrawals pending count') }}
                    </p>
                    <p class="text-lg font-semibold text-gray-700 dark:text-gray-200">
                        {{  locationStats.countOfWithdrawalsPending }}
                    </p>
                </div>
            </card>
        </div>
        <div class="sm:px-1 md:px-3 lg:px-3 xl:px-3 mb-1 sm:mb-1 md:mb-3 lg:mb-3 xl:mb-3 w-full sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/2">
            <card class="flex-1">
                <div class="mb-4">
                    <h2 class="font-bold">{{ $t('Summary per operator') }}</h2>
                </div>
                <div class="mb-4">
                    <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {{ $t('Deposits total sum') }}
                    </p>
                    <p v-for="(deposit, currency) in operatorStats.sumOfDeposits" class="text-lg font-semibold text-gray-700 dark:text-gray-200">
                        {{  showFormattedAmount(deposit, currency) }}
                    </p>
                </div>
                <div class="mb-4">
                    <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {{ $t('Withdrawals total sum') }}
                    </p>
                    <p v-for="(withdrawal, currency) in operatorStats.sumOfWithdrawals" class="text-lg font-semibold text-gray-700 dark:text-gray-200">
                        {{  showFormattedAmount(withdrawal, currency) }}
                    </p>
                </div>
                <div>
                    <p class="text-sm font-medium text-gray-600 dark:text-gray-400">
                        {{ $t('Withdrawals pending count') }}
                    </p>
                    <p class="text-lg font-semibold text-gray-700 dark:text-gray-200">
                        {{  operatorStats.countOfWithdrawalsPending }}
                    </p>
                </div>
            </card>
        </div>
        <div class="sm:px-1 md:px-3 lg:px-3 xl:px-3 mb-1 sm:mb-1 md:mb-3 lg:mb-3 xl:mb-3 w-full sm:w-full md:w-1/2 lg:w-1/2 xl:w-1/2">
            <card class="flex items-center">
                <div class="flex flex-wrap justify-center">
                    <div class="w-full mr-4"></div>
                    <btn-link class="my-4 mr-4" :href="route('players.index')">
                        {{ $t('Players List') }}
                    </btn-link>
                    <btn-link class="my-4 mr-4" :href="route('players.create')">
                        {{ $t('Create Player') }}
                    </btn-link>
                </div>
            </card>
        </div>
    </div>
</template>

<script>
import Card from '../Components/Card';
import BtnLink from '../Components/BtnLink';

export default {
    components: {BtnLink, Card },
    props: ['playerCount', 'balances', 'operatorStats', 'locationStats'],
    metaInfo() {
        return {
            title: this.title
        }
    },
    computed: {
        title () {
            return this.$t('Dashboard');
        }
    },
    methods: {
        showFormattedAmount(amount, currencyCode) {
            return this.$formatCurrency(amount, currencyCode);
        }
    }
}
</script>
